import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy, Mail, MapPin, MessageSquare, Share2, Twitter } from "lucide-react";
import { KindredMark } from "./KindredMark";
import { AmbientBackground } from "./AmbientBackground";
import { CelebrationOverlay } from "./CelebrationOverlay";

const STEPS = [
  {
    icon: Mail,
    title: "Check your inbox",
    body: "Your welcome email is on its way — it confirms your spot and your city.",
  },
  {
    icon: MapPin,
    title: "Watch your city climb",
    body: "Every signup pushes your city up the launch order toward the 1,000-member unlock.",
  },
  {
    icon: Share2,
    title: "Bring your people",
    body: "Friends who join through your link count double for your city — and the first 1,000 members earn Gold Status forever.",
  },
];

const ThankYouPage = () => {
  const { state } = useLocation();
  const email = state?.email || "";
  const city = state?.city || "";
  const refCode = state?.refCode || "";
  const [copied, setCopied] = useState(false);
  const [celebrating, setCelebrating] = useState(() => Boolean(state?.email));

  useEffect(() => {
    // Strip signup state from history so a refresh doesn't replay the celebration
    const s = window.history.state;
    if (s?.usr?.email) {
      window.history.replaceState({ ...s, usr: null }, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareUrl = refCode ? `${window.location.origin}/?ref=${refCode}` : "";
  const shareText = encodeURIComponent(
    "I'm on the waitlist for Community Hearts — the dating app for people who are done playing games. Join with my link and bump our city up the launch order:"
  );
  const shareTargets = [
    { id: "twitter", label: "Share on X", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}`, newTab: true },
    { id: "imessage", label: "Share via iMessage", icon: MessageSquare, href: `sms:&body=${shareText}%20${encodeURIComponent(shareUrl)}`, newTab: false },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (err) {
      console.warn("Clipboard unavailable", err);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cityLine =
    city && city !== "Not specified" && city !== "Somewhere else"
      ? `We'll notify you when Community Hearts launches in ${city}.`
      : "We'll notify you when Community Hearts launches in your city.";

  return (
    <div className="dark-landing min-h-screen overflow-x-clip bg-[#16233D] font-sans text-cream" data-testid="thank-you-page">
      <AmbientBackground />
      <AnimatePresence>
        {celebrating && <CelebrationOverlay city={city} onDone={() => setCelebrating(false)} />}
      </AnimatePresence>
      <header className="relative z-[1] border-b border-cream/10 bg-obsidian/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="thank-you-logo">
            <KindredMark className="h-7 w-7" />
            <span className="font-serif text-xl font-semibold tracking-tight text-cream">Community Hearts</span>
          </Link>
          <Link
            to="/"
            className="rounded-full border border-cream/20 px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-cream/70 transition-colors duration-300 hover:border-gold hover:text-gold"
            data-testid="thank-you-back-link"
          >
            Back to the site
          </Link>
        </div>
      </header>

      <main className="relative z-[1] mx-auto max-w-2xl px-6 py-20 text-center md:py-28">
        <motion.div
          initial={{ scale: 0.6 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gold"
          data-testid="thank-you-check"
        >
          <Check className="h-9 w-9 text-ink" />
        </motion.div>

        <h1 className="mt-8 font-serif text-4xl tracking-tight md:text-6xl" data-testid="thank-you-headline">
          You're on the list.
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-cream/70 md:text-xl" data-testid="thank-you-city-line">
          {cityLine}
        </p>
        {email && (
          <p className="mt-2 text-sm text-cream/50" data-testid="thank-you-email-line">
            Confirmation is headed to {email}.
          </p>
        )}

        <div className="mt-14 grid gap-4 text-left sm:grid-cols-3" data-testid="thank-you-steps">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-3xl border border-cream/10 bg-cream/5 p-6"
              data-testid={`thank-you-step-${i + 1}`}
            >
              <s.icon className="h-6 w-6 text-gold" />
              <p className="mt-4 font-serif text-lg tracking-tight">{s.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-cream/60">{s.body}</p>
            </motion.div>
          ))}
        </div>

        {shareUrl && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 rounded-3xl border border-gold/30 bg-gold/5 p-6 text-left md:p-8"
            data-testid="thank-you-share-box"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold" data-testid="thank-you-referral-note">
              Bump {city && city !== "Not specified" && city !== "Somewhere else" ? city : "your city"} up the launch order
            </p>
            <p className="mt-2 text-sm leading-relaxed text-cream/70">
              Every friend who joins through your personal link counts double for your city.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <code className="flex-1 truncate rounded-full border border-cream/20 px-4 py-2.5 text-xs text-cream/80" data-testid="thank-you-share-link">
                {shareUrl}
              </code>
              <button
                type="button"
                onClick={copyLink}
                data-testid="thank-you-copy-button"
                className="btn-glow flex items-center gap-1.5 rounded-full bg-gold px-4 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-ink hover:bg-gold-deep active:scale-95"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-cream/40">Or share directly</span>
              {shareTargets.map((t) => (
                <a
                  key={t.id}
                  href={t.href}
                  {...(t.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  aria-label={t.label}
                  title={t.label}
                  data-testid={`thank-you-share-${t.id}`}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-cream/20 text-cream/70 transition-colors duration-300 hover:border-gold hover:text-gold active:scale-95"
                >
                  <t.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ThankYouPage;
